<template>
  <vf-carousel-elevate
    v-if="items.length"
    ref="carouselRef"
    align="start"
    :class-container="[
      { [`lg:nowrap ${brandClasses.carousel}`]: alwaysUseGrid || items?.length > 1 },
      'lg:h-$h-lg',
    ]"
    data-test-id="vf-media-gallery"
    :drag-free="$viewport.lg"
    :loop="gallery.loop"
    :slides-to-scroll="1"
    :style-container
  >
    <template #default>
      <div
        v-for="({ item, sourceIndex }, i) in carouselItems"
        :key="`${item.type === 'espot' ? item.id : item.src}-${i}`"
        class="relative <lg:w-full"
        :class="[
          getItemClass
            ? getItemClass(sourceIndex)
            : { [brandClasses.carouselItemFirst || '']: i === 0 },
          brandClasses.carouselItem,
          { 'lg:pr-2': gallery.loop || i < items.length - 1 },
        ]"
      >
        <cms-section v-if="item.type === 'espot'" full-height :section="item.section" />
        <template v-if="item.type === 'image'">
          <vf-zoomer
            v-if="item.srcZoom"
            :alt="isAltTextRepeated ? `${item.alt} - ${i + 1}` : item.alt"
            :height="presets.height"
            :lazy="i >= 4"
            :props-img="{ class: imageClass }"
            :src="getImageSrc(item)"
            :src-zoom="item.srcZoom"
            :width="presets.width"
            @zoom="$zoomFactor => $emit('zoom', sourceIndex, $zoomFactor)"
          />
          <base-picture
            v-else
            :alt="isAltTextRepeated ? `${item.alt} - ${i + 1}` : item.alt"
            class="block w-full overflow-hidden"
            :height="presets.height"
            :lazy="i >= 4"
            :props-img="{ class: ['duration full', imageClass] }"
            :src="getImageSrc(item)"
            :width="presets.width"
          />
          <slot v-bind="{ i, item }" />
        </template>
        <vf-video
          v-if="item.type === 'video'"
          ref="videos"
          v-bind="{ ...item, ...getVideoSrc(item) }"
          class="full overflow-hidden rounded-lg"
          :controlslist
          fit="cover"
          @play="handlePlay(item)"
        />
        <sketchfab v-if="item.type === 'sketchfab' && enableSketchfab" :product-id="item.productId" />
      </div>
    </template>

    <template #prev="{ active, action }">
      <base-button
        :aria-label="$t.previousSlide"
        class="absolute bottom-4 left-4 z-2 rounded-xs bg-grey-90 bg-op-80 @hactive:c-grey-60 shadow-sm duration -scale-x-100"
        :class="[{ 'pointer-events-none op-0': !gallery.loop && !active, 'hidden': carouselItems.length < 2 }]"
        data-test-id="vf-carousel-arrow-left"
        @click.stop="action"
      >
        <vf-icon name="chevron" size="md" />
      </base-button>
    </template>

    <template #next="{ active, action }">
      <base-button
        :aria-label="$t.nextSlide"
        class="absolute bottom-4 left-12 z-2 rounded-xs bg-grey-90 bg-op-80 @hactive:c-grey-60 shadow-sm duration"
        :class="[{ 'pointer-events-none op-0': !gallery.loop && !active, 'hidden': carouselItems.length < 2 }]"
        data-test-id="vf-carousel-arrow-right"
        @click.stop="action"
      >
        <vf-icon name="chevron" size="md" />
      </base-button>
    </template>
  </vf-carousel-elevate>
  <div v-else class="bg-grey-80 py-1/2 text-center lh-0">
    {{ $t.imageUnavailable }}
  </div>
</template>

<script lang="ts" setup>
import type { BaseVideo as BaseVideoType } from '#components'
import type { Responsive } from '#types/common'
import type { CarouselElevateInstance } from '#types/components/vf/carousel-elevate'
import type { GalleryEspotItem, ImageItem, SketchfabItem, VideoItem } from '#types/media'

import type { EmblaCarouselType } from 'embla-carousel'

const { items } = defineProps<{
  items: (ImageItem | VideoItem | SketchfabItem | GalleryEspotItem)[]
  pagination?: boolean
  thumbnails?: boolean
  productId?: string
  nativePinchZoom?: boolean
  getItemClass?: (index: number) => string
}>()

defineEmits<{
  zoom: [index: number, zoomFactor: number]
}>()

defineSlots<{
  default: (props: { i: number, item: ImageItem }) => void
}>()

const {
  alwaysUseGrid,
  brandClasses,
  controlslist
} = useAppConfig().components.vf.mediaGallery
const { enableSketchfab, gallery } = useAppConfig().pages?.pdp || {}
const header = useHeaderStore()
const { $viewport } = useNuxtApp()
const { y } = isClient() ? useScroll(document) : { y: ref(0) }
const stablePromoHeight = ref(0)
const initialGalleryHeight = 'max(40rem, calc(100dvh - 9.425rem))'

const INITIAL_LOOP_ITEM_COUNT = 4
const LOOP_RECALCULATION_THROTTLE = 300
const loopItemCount = ref(INITIAL_LOOP_ITEM_COUNT)
const carouselRef = useTemplateRef<CarouselElevateInstance>('carouselRef')
const carouselRoot = ref<HTMLElement>()

const imageClass = 'max-w-full w-full rounded-lg contain lg:h-[var(--gallery-h)] lg:w-a transition-opacity'

const carouselItems = computed(() => {
  if (!items.length) return []

  const repetitions = gallery.loop ? Math.ceil(loopItemCount.value / items.length) : 1
  return Array.from({ length: repetitions * items.length }, (_, index) => {
    const sourceIndex = index % items.length
    return { item: items[sourceIndex], sourceIndex }
  })
})

const getEmblaApi = () => toValue(carouselRef.value?.emblaApi)

const recalculateLoopItemCount = async () => {
  if (!gallery.loop || !items.length) return

  const emblaApi = getEmblaApi()
  const slides = emblaApi?.slideNodes()
  const viewportWidth = emblaApi?.rootNode().getBoundingClientRect().width

  if (!emblaApi || !slides || !viewportWidth || slides.length <= items.length) return

  const sourceSlideSizes = slides.slice(0, items.length).map((slide, index) =>
    slides[index + 1].offsetLeft - slide.offsetLeft
  )
  const sourceCycleWidth = sourceSlideSizes.reduce((total, width) => total + width, 0)

  if (!sourceCycleWidth) return

  const repetitions = Math.ceil(
    (viewportWidth + Math.max(...sourceSlideSizes)) / sourceCycleWidth
  )

  const nextLoopItemCount = Math.max(INITIAL_LOOP_ITEM_COUNT, repetitions * items.length)

  if (nextLoopItemCount === loopItemCount.value) return

  loopItemCount.value = nextLoopItemCount
  await nextTick()
  emblaApi.reInit()
}

const updateLoopItemCount = useThrottleFn(
  recalculateLoopItemCount,
  LOOP_RECALCULATION_THROTTLE,
  true,
  false
)

useResizeObserver(carouselRoot, updateLoopItemCount)

let attachedEmblaApi: EmblaCarouselType | undefined

const attachCarousel = async (emblaApi: EmblaCarouselType | undefined) => {
  if (!emblaApi || attachedEmblaApi === emblaApi) return

  attachedEmblaApi?.off('reInit', updateLoopItemCount)
  attachedEmblaApi = emblaApi
  carouselRoot.value = emblaApi.rootNode()
  emblaApi.on('reInit', updateLoopItemCount)
  await recalculateLoopItemCount()
}

watch(getEmblaApi, attachCarousel)

onBeforeUnmount(() => attachedEmblaApi?.off('reInit', updateLoopItemCount))

watch(() => items, async () => {
  loopItemCount.value = INITIAL_LOOP_ITEM_COUNT
  await nextTick()
  await recalculateLoopItemCount()
})

const visibleHeaderHeight = computed(() => {
  if (!isClient()) return 0
  const promoVisible = Math.max(0, stablePromoHeight.value - y.value)
  return header.height.header + promoVisible
})

const enhancedGalleryHeight = computed(() => {
  if (!isClient() || !$viewport.lg) return
  if (!header.height.header) return
  return `max(40rem, calc(100dvh - ${pxToRem(visibleHeaderHeight.value + 50)}))`
})

const styleContainer = computed(() => ({
  '--h-lg': initialGalleryHeight,
  '--gallery-h': enhancedGalleryHeight.value ?? initialGalleryHeight,
  'height': enhancedGalleryHeight.value,
}))

watch(
  () => header.height.promoBar,
  (val) => { if (!stablePromoHeight.value && val > 0) stablePromoHeight.value = val },
  { immediate: true }
)

const getVideoSrc = (item: VideoItem) => {
  const entries = Object.entries(gallery.videoSource ?? gallery.imageSource).map(([breakpoint, key]) => {
    const { width, height } = gallery.presets[key]
    const src = getVideoTransformations(item.src, {
      width,
      height,
    })
    return { breakpoint, src, width, height }
  })
  return {
    src: Object.fromEntries(entries.map(({ breakpoint, src }) => [breakpoint, src])) as unknown as Responsive,
    width: Object.fromEntries(
      entries.map(({ breakpoint, width }) => [breakpoint, width])
    ) as unknown as Responsive<number>,
    height: Object.fromEntries(
      entries.map(({ breakpoint, height }) => [breakpoint, height])
    ) as unknown as Responsive<number>,
  }
}

const isAltTextRepeated = computed(() => carouselItems.value.length > 1
  && carouselItems.value.every(({ item }) => item.alt === carouselItems.value[0].item.alt))

const presets = computed(() => Object.entries(gallery.imageSource).reduce(
  (acc, [breakpoint, key]) => {
    const { width, height } = gallery.presets[key] ?? gallery.presets.src
    acc.width[breakpoint as keyof Responsive] = width
    acc.height[breakpoint as keyof Responsive] = height
    return acc
  },
  { width: {} as Responsive, height: {} as Responsive }
))

const getImageSrc = (item) => Object.fromEntries(
  Object.entries(gallery.imageSource).map(([breakpoint, key]) => [
    breakpoint,
    item[key as keyof typeof item] || item.src
  ])
) as Responsive

const videos = ref<typeof BaseVideoType[]>([])

const handlePlay = ({ src }) => {
  const resolvedSrc = getValueForBreakpoint($viewport.breakpoint, src)
  const startingVideo = videos.value.find(
    ({ videoRef }) => getValueForBreakpoint($viewport.breakpoint, videoRef.src) === resolvedSrc
  )
  if (startingVideo && !startingVideo.videoRef.el.muted) {
    videos.value
      .filter((video) => video !== startingVideo && !video.videoRef.el.muted)
      .forEach((video) => video.pause())
  }
}
</script>
